import { Question, QuestionResponse, Section } from 'features/sessions/types';

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'store';
import { useGetSessionQuery, useEndSessionMutation, usePutSelectionMutation, usePutQuestionResponseMutation, useReportQuestionMutation } from 'features/sessions/api';
import { updateQuestionId, optimisticCompleteQuestion } from 'features/sessions/reducers';
import * as selectors from 'features/sessions/selectors';
import { Loading } from 'components';
import { SessionSectionType } from './Session.constants';
import type { ReviewSubset } from './elements/Footer/Footer.types';
import * as modals from 'constants/modals';
import {
  Header,
  SubHeader,
  Footer,
  QuestionSection,
  QuestionChips,
  PackageInstruction,
  SectionInstruction,
  ReviewPrescore,
  OverviewSection,
  Modal,
  Calculator,
  Navigator,
  SubHeaderButton,
} from './elements';
import * as routes from 'constants/routes';
import './_session.scss';

export const Session = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { session_id = '' } = useParams<{ session_id: string }>();

  const { isLoading, isError, refetch: refetchSession } = useGetSessionQuery({ session_id });
  const [endSession, { isLoading: isEndingSession }] = useEndSessionMutation();
  const [navigateToSection, { isLoading: isNavigatingSection }] = usePutSelectionMutation();
  const [reportQuestion, { isLoading: isReportingQuestion }] = useReportQuestionMutation();
  const [answerToQuestion] = usePutQuestionResponseMutation();

  const currentSession = useSelector(selectors.selectCurrentSession);
  const currentQuestionResponse = useSelector(selectors.selectCurrentQuestionResponse);
  const currentSection = useSelector(selectors.selectCurrentSection);
  const nextSection = useSelector(selectors.selectNextSection);
  const prevQuestionId = useSelector(selectors.selectPrevQuestionId);
  const nextQuestionId = useSelector(selectors.selectNextQuestionId);
  const currentPackage = useSelector(selectors.selectCurrentPackage);

  /*************************
   * Review Prescore (end-of-section review screen)
   * Local state only — never in Redux — so a getSession refetch cannot knock
   * the user off the review screen (issue-#25 race class). Reset whenever the
   * section changes (see handleSectionChange + the defensive effect below).
   *************************/
  const [reviewState, setReviewState] = useState<
    { phase: 'summary' } | { phase: 'reviewing'; subset: ReviewSubset } | null
  >(null);
  const reviewPhase = reviewState?.phase ?? null;
  // Empty-subset guard: a Review button whose subset has no questions shows
  // an info dialog and stays on the summary (the buttons are never disabled).
  const [reviewEmptyMessage, setReviewEmptyMessage] = useState<{ title: string; body: string } | null>(null);

  // Subset predicate (live-recomputed): all = every question; incomplete =
  // anything NOT Completed (Unseen ∪ InComplete); flagged = flagged.
  const reviewMatches = useCallback(
    (q: Section['questions'][number], subset: ReviewSubset) =>
      subset === 'all' ? true : subset === 'incomplete' ? q.status !== 'Completed' : Boolean(q.flagged),
    [],
  );

  // First/next/prev question ids for the active subset, scanned positionally
  // over the ordered question list relative to the current question. Recomputed
  // on every status/flag change so it always reflects post-answer truth.
  const reviewNav = useMemo(() => {
    if (reviewState?.phase !== 'reviewing' || !currentSection) {
      return { nextId: null as Question['id'] | null, prevId: null as Question['id'] | null };
    }
    const qs = currentSection.questions;
    const curIdx = qs.findIndex((q) => q.id === currentSession.question_id);
    let nextId: Question['id'] | null = null;
    let prevId: Question['id'] | null = null;
    if (curIdx !== -1) {
      for (let j = curIdx + 1; j < qs.length; j++) {
        if (reviewMatches(qs[j], reviewState.subset)) { nextId = qs[j].id; break; }
      }
      for (let j = curIdx - 1; j >= 0; j--) {
        if (reviewMatches(qs[j], reviewState.subset)) { prevId = qs[j].id; break; }
      }
    }
    return { nextId, prevId };
  }, [reviewState, currentSection, currentSession.question_id, reviewMatches]);

  /*************************
   * Answer Submission Debounce
   *************************/
  const pendingValue = useRef<QuestionResponse['value'] | undefined>(undefined);
  const pendingQuestionId = useRef<Question['id'] | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout>>();

  const flushPendingAnswer = useCallback(() => {
    clearTimeout(debounceTimer.current);
    debounceTimer.current = undefined;
    if (pendingValue.current !== undefined && pendingQuestionId.current) {
      answerToQuestion({
        session_id,
        question_id: pendingQuestionId.current,
        value: pendingValue.current,
        flagged: Boolean(currentQuestionResponse?.flagged),
      });
      pendingValue.current = undefined;
      pendingQuestionId.current = null;
    }
  }, [session_id, answerToQuestion, currentQuestionResponse?.flagged]);

  const submitResponse = useCallback(
    (value: QuestionResponse['value']) => {
      pendingValue.current = value;
      pendingQuestionId.current = currentSession.question_id;
      // Optimistically mark this question Completed so a live subset predicate
      // (Review Incomplete) drops it immediately — the PUT reconciles after.
      if (value !== null && value !== '' && currentSession.question_id) {
        dispatch(optimisticCompleteQuestion(currentSession.question_id));
      }
      clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(flushPendingAnswer, 150);
    },
    [currentSession.question_id, flushPendingAnswer, dispatch],
  );

  const submitFlagToggle = useCallback(() => {
    if (!currentQuestionResponse) return;
    clearTimeout(debounceTimer.current);
    debounceTimer.current = undefined;
    const value = pendingValue.current !== undefined
      ? pendingValue.current
      : currentQuestionResponse.value;
    pendingValue.current = undefined;
    pendingQuestionId.current = null;
    answerToQuestion({
      session_id,
      question_id: currentQuestionResponse.question_id,
      value,
      flagged: !currentQuestionResponse.flagged,
    });
  }, [session_id, answerToQuestion, currentQuestionResponse]);

  // Stable ref-wrapper so useMemo deps don't change when submitResponse changes
  const submitResponseRef = useRef(submitResponse);
  submitResponseRef.current = submitResponse;
  const stableSubmitResponse = useCallback(
    (value: QuestionResponse['value']) => submitResponseRef.current(value),
    [],
  );

  /*************************
   * Package Instruction States
   *************************/
  const [packageInstructionIndex, setPackageInstructionIndex] = useState<number>(0);

  /*************************
   * Modal States
   *************************/
  const [isExplainModalOpen, setIsExplainModalOpen] = useState<boolean>(false);
  const [isCalculatorModalOpen, setIsCalculatorModalOpen] = useState<boolean>(false);
  const [isBeginExamModalOpen, setIsBeginExamModalOpen] = useState<boolean>(false);
  const [isEndExamModalOpen, setIsEndExamModalOpen] = useState<boolean>(false);
  const [isEndSectionModalOpen, setIsEndSectionModalOpen] = useState<boolean>(false);
  const [isNavigatorModalOpen, setIsNavigatorModalOpen] = useState<boolean>(false);
  const [isTimeExpiredModalOpen, setIsTimeExpiredModalOpen] = useState<boolean>(false);
  const [isReportQuestionModalOpen, setIsReportQuestionModalOpen] = useState(false);
  const [isInstructionsModalOpen, setIsInstructionsModalOpen] = useState(false);
  // End Review is a two-stage confirm: 0 = closed, 1 = first dialog,
  // 2 = "End Review Confirmation" dialog.
  const [endReviewStage, setEndReviewStage] = useState<0 | 1 | 2>(0);
  const [reportReasons, setReportReasons] = useState<string[]>([]);
  const [reportText, setReportText] = useState('');

  const isHotkeyDisabled =
    isExplainModalOpen || isBeginExamModalOpen || isEndExamModalOpen || isEndSectionModalOpen || isTimeExpiredModalOpen || isReportQuestionModalOpen || isCalculatorModalOpen || isInstructionsModalOpen || endReviewStage > 0;

  const handleExplainModalToggle = (isOpen: boolean) => () => setIsExplainModalOpen(isOpen);
  const handleInstructionsModalToggle = (isOpen: boolean) => () => setIsInstructionsModalOpen(isOpen);
  const handleCalculatorModalToggle = (isOpen: boolean) => () => setIsCalculatorModalOpen(isOpen);
  const handleCalculatorToggle = () => setIsCalculatorModalOpen((prev) => !prev);
  const handleBeginExamModalToggle = (isOpen: boolean) => () => setIsBeginExamModalOpen(isOpen);
  const handleEndExamModalToggle = (isOpen: boolean) => () => setIsEndExamModalOpen(isOpen);
  const handleReportQuestionToggle = (isOpen: boolean) => () => setIsReportQuestionModalOpen(isOpen);
  const handleEndSectionModalToggle = (isOpen: boolean) => () => setIsEndSectionModalOpen(isOpen);
  const handleNavigatorModalToggle = (isOpen: boolean) => () => setIsNavigatorModalOpen(isOpen);
  const handleTimeExpiredModalToggle = (isOpen: boolean) => () => setIsTimeExpiredModalOpen(isOpen);

  const handleBeginExamConfirm = () => {
    handleBeginExamModalToggle(false)();
    setPackageInstructionIndex(1);
  };

  const handleEndExamConfirm = () => {
    handleEndExamModalToggle(false)();
    navigate(routes.HOME);
  };

  const handleEndSectionConfirm = async () => {
    if (isTimeExpiredModalOpen)
      await handleTimeExpiredModalToggle(false)();
    await handleEndSectionModalToggle(false)();
    await handleSectionChange(nextSection?.id ?? null);
  };

  const handleTimeExpiredConfirm = async () => {
    if (isEndSectionModalOpen)
      await handleEndSectionModalToggle(false)();
    await handleTimeExpiredModalToggle(false)();
    await handleSectionChange(nextSection?.id ?? null);
  };

  // const ToggleButton = ({ label }: { label: string }) => {
  //   const isActive = reportReasons.includes(label);
  
  //   const toggleReason = () => {
  //     setReportReasons((prev) =>
  //       isActive ? prev.filter((item) => item !== label) : [...prev, label]
  //     );
  //   };
  
  //   return (
  //     <button
  //       onClick={toggleReason}
  //       className={`tag-button ${isActive ? 'active' : ''}`}
  //     >
  //       {label}
  //     </button>
  //   );
  // };


  useEffect(() => {
    isError && !currentSession.id && navigate(routes.RESTRICTED);
  }, [isError, currentSession.id, navigate]);

  // Defensive reset: whatever path moves the session to a different section
  // (explicit handler, navigateToSection settling, or a getSession refetch),
  // never carry a stale Review Prescore screen into the new section.
  // A confirmation chain can fire more than one section advance (e.g. the End
  // Review confirm immediately followed by the Time Expired modal's OK).
  // `nextSection` is recomputed relative to the current section, so a second
  // advance would skip a whole section (VR -> DM -> QR). This latch is raised
  // by handleSectionChange and held — across the async settle AND any human
  // click latency in between — until the section actually changes, so neither
  // concurrent nor sequential double-advances can slip through.
  const sectionAdvanceRequestedRef = useRef(false);
  const prevSectionIdRef = useRef(currentSession.section_id);
  useEffect(() => {
    if (prevSectionIdRef.current !== currentSession.section_id) {
      prevSectionIdRef.current = currentSession.section_id;
      setReviewState(null);
      // The End Review confirmation is part of the Review Prescore screen
      // state; never let a pending stage bleed into the new section.
      setEndReviewStage(0);
      // The advance landed; release the latch for the next section.
      sectionAdvanceRequestedRef.current = false;
    }
  }, [currentSession.section_id]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        e.stopPropagation();
      }
      // Prevent Alt from activating the browser menu bar/chrome UI
      if (e.key === 'Alt') {
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleQuestionChange = useCallback(
    (questionId: Question['id'] | null) => {
      if (questionId) {
        flushPendingAnswer();
        dispatch(updateQuestionId(questionId));
      }
    },
    [dispatch, flushPendingAnswer],
  );

  const handleSectionChange = useCallback(
    async (sectionId: Section['id'] | null) => {
      // Latched in sectionAdvanceRequestedRef (declared above, beside the
      // defensive section-change effect that releases it once the section
      // actually changes) so a second confirmation cannot advance again.
      if (sectionAdvanceRequestedRef.current) {
        return;
      }
      sectionAdvanceRequestedRef.current = true;
      flushPendingAnswer();
      try {
        if (sectionId) {
          await navigateToSection({ session_id, section_id: sectionId }).unwrap();
          refetchSession();
        } else {
          await endSession({ session_id }).unwrap();
          if (currentSession.redirect_url) {
            window.location.href = currentSession.redirect_url;
          } else {
            refetchSession();
          }
        }
      } catch (error) {
        console.error('Error handling section change:', error);
        // The advance failed; release the latch so the user can retry.
        sectionAdvanceRequestedRef.current = false;
      }
    },
    [session_id, currentSession.redirect_url, navigateToSection, endSession, refetchSession, flushPendingAnswer],
  );

  const handleNavigatePrev = () => {
    if (reviewState?.phase === 'reviewing') {
      // Subset-filtered Previous; absent when no earlier match (Footer hides it).
      if (reviewNav.prevId) handleQuestionChange(reviewNav.prevId);
      return;
    }
    if (prevQuestionId) {
      handleQuestionChange(prevQuestionId);
    }
  };

  const handleNavigateNext = () => {
    if (reviewState?.phase === 'reviewing') {
      // Subset-filtered Next; exhausting the subset returns to the summary.
      flushPendingAnswer();
      if (reviewNav.nextId) {
        handleQuestionChange(reviewNav.nextId);
      } else {
        setReviewState({ phase: 'summary' });
      }
      return;
    }
    if (sectionType === SessionSectionType.PACKAGE_INSTRUCTION) {
      if (packageInstructionIndex === 0) {
        handleBeginExamModalToggle(true)();
      } else if (packageInstructionIndex < 4) {
        setPackageInstructionIndex((currentIndex) => currentIndex + 1);
      } else if (packageInstructionIndex === 4) {
        handleSectionChange(nextSection?.id ?? null);
      }
    } else if (nextQuestionId) {
      handleQuestionChange(nextQuestionId);
    } else if (sectionType === SessionSectionType.QUESTION) {
      // Last question of the subtest: open the Review Prescore screen
      // (previously this looped back to question 1 — issue #45).
      flushPendingAnswer();
      setReviewState({ phase: 'summary' });
    }
  };

  // End Review always double-confirms (matching the real exam): the button
  // opens the first dialog, not the section change.
  const handleEndReview = useCallback(() => {
    setEndReviewStage(1);
  }, []);

  const handleEndReviewConfirm = useCallback(() => {
    // Clear review state up-front (explicit reset path) so the next section
    // never inherits a stale Review Prescore screen, then advance exactly as
    // the existing end-of-section flow does.
    setEndReviewStage(0);
    setReviewState(null);
    handleSectionChange(nextSection?.id ?? null);
  }, [handleSectionChange, nextSection?.id]);

  // Per-row "Question N" click on the summary: jump to that question and
  // review it with normal all-question traversal (NOT a filtered subset —
  // the subset buttons land in #47).
  const handleReviewQuestion = useCallback(
    (questionId: Question['id']) => {
      setReviewState({ phase: 'reviewing', subset: 'all' });
      handleQuestionChange(questionId);
    },
    [handleQuestionChange],
  );

  // "Review Screen" — leave the question and return to the summary list.
  const handleReviewScreen = useCallback(() => {
    flushPendingAnswer();
    setReviewState({ phase: 'summary' });
  }, [flushPendingAnswer]);

  // Review All / Incomplete / Flagged buttons: enter subset review at the
  // first matching question. Empty subset → info dialog, stay on summary.
  const handleReviewSubset = useCallback(
    (subset: ReviewSubset) => {
      const qs = currentSection?.questions ?? [];
      const first = qs.find((q) =>
        subset === 'all' ? true : subset === 'incomplete' ? q.status !== 'Completed' : Boolean(q.flagged),
      );
      if (!first) {
        setReviewEmptyMessage(
          subset === 'flagged'
            ? { title: 'No Questions Flagged', body: 'No questions have been flagged for review.' }
            : { title: 'No Incomplete Questions', body: 'There are no incomplete questions to review.' },
        );
        return;
      }
      setReviewState({ phase: 'reviewing', subset });
      handleQuestionChange(first.id);
    },
    [currentSection, handleQuestionChange],
  );

  // Toggle a question's flag directly from the summary list. value:null with
  // a flag change hits the backend's flag-only smart-merge, which preserves
  // the stored answer (it does NOT overwrite or clear it).
  const handleFlagQuestion = useCallback(
    (questionId: Question['id'], flagged: boolean) => {
      answerToQuestion({ session_id, question_id: questionId, value: null, flagged: !flagged });
    },
    [answerToQuestion, session_id],
  );

  const handleReturnToOverview = () => {
    handleSectionChange(null);
  };

  const handleTimeExpired = () => {
    // Time expiry unconditionally supersedes an open End Review confirmation
    // (either stage): Pearson replaces any pending dialog with its single Time
    // Expired notice rather than stacking a second modal on top of it.
    if (endReviewStage > 0) {
      setEndReviewStage(0);
    }

    if (
      sectionType === SessionSectionType.PACKAGE_INSTRUCTION ||
      sectionType === SessionSectionType.SECTION_INSTRUCTION
    ) {
      handleNavigateNext();
    } else if (
      sectionType === SessionSectionType.QUESTION ||
      sectionType === SessionSectionType.REVIEW_PRESCORE
    ) {
      // Expiry on the Review Prescore summary behaves exactly like expiry on a
      // question: the Time Expired modal, then advance to the next section.
      handleTimeExpiredModalToggle(true)();
    }
  };

  const sectionType = useMemo(() => {
    // Review Prescore is driven by local state and takes precedence over the
    // server-derived type, so a getSession refetch cannot knock the user off
    // the screen mid-review.
    if (reviewState?.phase === 'summary') {
      return SessionSectionType.REVIEW_PRESCORE;
    }

    const { completed, section_id, question_id } = currentSession;

    if (completed) {
      if (question_id) {
        return SessionSectionType.QUESTION;
      }

      if (section_id) {
        return SessionSectionType.SECTION_INSTRUCTION;
      }

      return SessionSectionType.OVERVIEW;
    }

    if (question_id) {
      return SessionSectionType.QUESTION;
    }

    if (section_id) {
      return SessionSectionType.SECTION_INSTRUCTION;
    }

    return SessionSectionType.PACKAGE_INSTRUCTION;
  }, [currentSession.completed, currentSession.section_id, currentSession.question_id, reviewState]);

  const sessionContent = useMemo(() => {
    switch (sectionType) {
      case SessionSectionType.PACKAGE_INSTRUCTION:
        return <PackageInstruction index={packageInstructionIndex} />;
      case SessionSectionType.SECTION_INSTRUCTION:
        return currentSession.section_id ? <SectionInstruction sectionId={currentSession.section_id} /> : null;
      case SessionSectionType.QUESTION:
        return currentSession.question_id ? (
          <>
            <QuestionSection
              sessionId={session_id}
              questionId={currentSession.question_id}
              isSessionCompleted={Boolean(currentSession.completed)}
              isHotkeyDisabled={isHotkeyDisabled}
              onSubmitResponse={stableSubmitResponse}
            />
            {Boolean(currentSession.completed) && <QuestionChips onQuestionChange={handleQuestionChange} />}
          </>
        ) : null;
      case SessionSectionType.REVIEW_PRESCORE:
        return <ReviewPrescore onReviewQuestion={handleReviewQuestion} onFlagToggleQuestion={handleFlagQuestion} />;
      case SessionSectionType.OVERVIEW:
        return <OverviewSection onQuestionChange={handleQuestionChange} />;
      default:
        return null;
    }
  }, [sectionType, currentSession.section_id, currentSession.question_id, session_id, packageInstructionIndex, isHotkeyDisabled, handleReviewQuestion, handleFlagQuestion]);

  const handleReport = async () => {
    try {
      const response = await reportQuestion({
        session_id: session_id,
        package_id: currentPackage?.id,
        section_id: currentSession?.section_id,
        question_id: currentSession?.question_id,
        category: reportReasons?.join(', '),
        details: reportText

      }).unwrap(); // unwrap() to directly access success/failure
      if (response.success) {
        setReportText('')
        setReportReasons([])
      } else
        console.log(response.message)
      setIsReportQuestionModalOpen(false)
    } catch (err) {
      console.error('Report failed:', err);
    }
  };

  function handleCloseReport() {
    setIsReportQuestionModalOpen(false)
    setReportText('')
    setReportReasons([])
  }

  if (isLoading || !currentSession.id) {
    return <Loading />;
  }

  // End Review copy is dynamic on the not-Completed count (Unseen ∪ InComplete).
  const endReviewIncomplete = currentSection
    ? currentSection.questions.filter((q) => q.status !== 'Completed').length
    : 0;
  const endReviewWarning =
    endReviewIncomplete > 0
      ? `You have chosen to end the current review, but have ${endReviewIncomplete} incomplete ` +
        `question${endReviewIncomplete === 1 ? '' : 's'}. If you click Yes, you will NOT be able to return to ` +
        `this review and answer the ${endReviewIncomplete} question${endReviewIncomplete === 1 ? '' : 's'} ` +
        `you have not completed.`
      : 'You have chosen to end the current review. If you click Yes, you will NOT be able to return to this review.';

  return (
    <>
      <div className="session__container">
        <Header
          onTimeExpired={handleTimeExpired}
          isSessionCompleted={Boolean(currentSession.completed)}
          sectionType={sectionType}
          packageInstructionIndex={packageInstructionIndex}
        />
        <SubHeader
          sectionType={sectionType}
          isSessionCompleted={Boolean(currentSession.completed)}
          isHotkeyDisabled={isHotkeyDisabled || isNavigatorModalOpen}
          onExplainModalToggle={handleExplainModalToggle}
          onCalculatorModalToggle={handleCalculatorModalToggle}
          onCalculatorToggle={handleCalculatorToggle}
          onFlagToggle={submitFlagToggle}
          onInstructionsToggle={handleInstructionsModalToggle}
        />
        <div className="session__content">{sessionContent}</div>
        <Footer
          sectionType={sectionType}
          reviewPhase={reviewPhase}
          reviewHasPrev={Boolean(reviewNav.prevId)}
          isSessionCompleted={Boolean(currentSession.completed)}
          isHotkeyDisabled={isHotkeyDisabled}
          onReviewScreen={handleReviewScreen}
          onReviewSubset={handleReviewSubset}
          onNavigatePrev={handleNavigatePrev}
          onNavigateNext={handleNavigateNext}
          onReturnToOverview={handleReturnToOverview}
          onEndExamModalToggle={handleEndExamModalToggle}
          onReportQuestionToggle={handleReportQuestionToggle}
          onEndSectionModalToggle={handleEndSectionModalToggle}
          onNavigatorModalToggle={handleNavigatorModalToggle}
          onEndReview={handleEndReview}
        />
        {(isNavigatingSection || isEndingSession) && <Loading />}
      </div>

      {/* Modals */}
      {isInstructionsModalOpen && (
        <Modal
          title="Instructions"
          primaryButtonText={modals.MODAL_BUTTON_TYPES.Close}
          onPrimaryButtonClick={handleInstructionsModalToggle(false)}
          onClose={handleInstructionsModalToggle(false)}
        >
          <div style={{ textAlign: 'left' }}>
            <p>Below is a summary of your answers. You can review your questions in three (3) different ways.</p>
            <p>The buttons in the lower right-hand corner correspond to these choices:</p>
            <ol>
              <li>Review all of your questions and answers.</li>
              <li>Review questions that are incomplete.</li>
              <li>
                Review questions that are flagged for review. (Click the &lsquo;flag&rsquo; icon to change the flag for
                review status.)
              </li>
            </ol>
            <p>You may also click on a question number link directly to its location in the exam.</p>
          </div>
        </Modal>
      )}
      {reviewEmptyMessage && (
        <Modal
          title={reviewEmptyMessage.title}
          primaryButtonText={modals.MODAL_BUTTON_TYPES.Close}
          onPrimaryButtonClick={() => setReviewEmptyMessage(null)}
          onClose={() => setReviewEmptyMessage(null)}
        >
          <div style={{ textAlign: 'left' }}>{reviewEmptyMessage.body}</div>
        </Modal>
      )}
      {isCalculatorModalOpen && (
        <Modal
          className="calculator__modal"
          title={<SubHeaderButton type="calculator" isHotkeyDisabled={isHotkeyDisabled} />}
          onClose={handleCalculatorModalToggle(false)}
        >
          <Calculator />
        </Modal>
      )}

      {isExplainModalOpen && (
        <Modal
          className="explain__modal"
          title={<SubHeaderButton type="answer" isHotkeyDisabled={isHotkeyDisabled} />}
          onClose={handleExplainModalToggle(false)}
        >
          <div dangerouslySetInnerHTML={{ __html: currentQuestionResponse?.question.explanation ?? '' }} />
        </Modal>
      )}

      {isReportQuestionModalOpen && (
        <Modal
          title={<span style={{ fontWeight: 'bold', fontSize: 18 }}>Improve this question</span>}
          primaryButtonText={modals.MODAL_BUTTON_TYPES.SubmitSuggestions}
          onPrimaryButtonClick={() => handleReport()}
          secondaryButtonText={modals.MODAL_BUTTON_TYPES.Close}
          onSecondaryButtonClick={() => handleCloseReport()}
          onClose={() => setIsReportQuestionModalOpen(false)}
        >
          <p style={{ fontWeight: 500, marginBottom: '1rem', textAlign: 'left' }}>
            What is the main problem with this question?
          </p>

          <div className="report-reason-group" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '16px', alignItems: 'center', pointerEvents: 'all' }}>
            {[
              'Question is in the wrong subsection',
              'None of the answers are correct',
              'Spelling/grammar problem',
              'Something wrong with images or stimulus'
            ].map((label) => {
              const isActive = reportReasons.includes(label);
              return (
                <button
                  key={label}
                  className={`reason-button ${isActive ? 'active' : ''}`}
                  style={{
                    border: '1px solid #5E98D9',
                    backgroundColor: isActive ? '#5E98D9' : '#fff',
                    color: isActive ? '#fff' : '#5E98D9',
                    padding: '6px 12px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    height: 'auto'
                  }}
                  onClick={() => {
                    setReportReasons((prev) =>
                      prev.includes(label) ? prev.filter((r) => r !== label) : [...prev, label]
                    );
                  }}
                >
                  + {label}
                </button>
              );
            })}
          </div>

          <textarea
            placeholder="Add more details here..."
            style={{
              width: '97%',
              height: '100px',
              padding: '10px',
              borderRadius: '4px',
              border: '1px solid #ccc',
              marginBottom: '16px',
              pointerEvents: 'all'
            }}
            value={reportText}
            onChange={(e) => setReportText(e.target.value)}
          />

          {isReportingQuestion &&
            <Loading />
          }
        </Modal>
      )}


      {isBeginExamModalOpen && (
        <Modal
          title="Ready to Begin Exam"
          primaryButtonText={modals.MODAL_BUTTON_TYPES.Yes}
          secondaryButtonText={modals.MODAL_BUTTON_TYPES.No}
          onPrimaryButtonClick={handleBeginExamConfirm}
          onSecondaryButtonClick={handleBeginExamModalToggle(false)}
          onClose={handleBeginExamModalToggle(false)}
        >
          If you are ready to begin the exam, select the Yes button. Otherwise, select the No button to return to the
          previous screen.
        </Modal>
      )}

      {isEndExamModalOpen && (
        <Modal
          title="End Exam"
          primaryButtonText={modals.MODAL_BUTTON_TYPES.Yes}
          secondaryButtonText={modals.MODAL_BUTTON_TYPES.No}
          onPrimaryButtonClick={handleEndExamConfirm}
          onSecondaryButtonClick={handleEndExamModalToggle(false)}
          onClose={handleEndExamModalToggle(false)}
        >
          You have chosen to end this exam. Are you sure you want to end this exam?
        </Modal>
      )}

      {isEndSectionModalOpen && (
        <Modal
          title="End Section"
          primaryButtonText={modals.MODAL_BUTTON_TYPES.Yes}
          secondaryButtonText={modals.MODAL_BUTTON_TYPES.No}
          onPrimaryButtonClick={handleEndSectionConfirm}
          onSecondaryButtonClick={handleEndSectionModalToggle(false)}
          onClose={handleEndSectionModalToggle(false)}
        >
          You have chosen to end the current section. If you click Yes, you will NOT be able to return to this section.{' '}
          <br />
          <br />
          Are you sure you want to end this section?
        </Modal>
      )}
      {endReviewStage === 1 && (
        <Modal
          title="End Review"
          primaryButtonText={modals.MODAL_BUTTON_TYPES.Yes}
          secondaryButtonText={modals.MODAL_BUTTON_TYPES.No}
          onPrimaryButtonClick={() => setEndReviewStage(2)}
          onSecondaryButtonClick={() => setEndReviewStage(0)}
          onClose={() => setEndReviewStage(0)}
        >
          {endReviewWarning}
          <br />
          <br />
          Are you sure you want to end this review?
        </Modal>
      )}
      {endReviewStage === 2 && (
        <Modal
          title="End Review Confirmation"
          primaryButtonText={modals.MODAL_BUTTON_TYPES.Yes}
          secondaryButtonText={modals.MODAL_BUTTON_TYPES.No}
          onPrimaryButtonClick={handleEndReviewConfirm}
          onSecondaryButtonClick={() => setEndReviewStage(0)}
          onClose={() => setEndReviewStage(0)}
        >
          Please confirm that you want to end this review.
          {endReviewIncomplete > 0
            ? ` If you click Yes, you will NOT be able to return to this review and answer the ${endReviewIncomplete} ` +
              `question${endReviewIncomplete === 1 ? '' : 's'} you have not completed.`
            : ' If you click Yes, you will NOT be able to return to this review.'}
        </Modal>
      )}

      {isNavigatorModalOpen && (
        <Modal
          className="navigator__modal"
          title="Navigator - select a question to go to it"
          primaryButtonText={modals.MODAL_BUTTON_TYPES.Close}
          onPrimaryButtonClick={handleNavigatorModalToggle(false)}
          onClose={handleNavigatorModalToggle(false)}
        >
          <Navigator navigateTo={handleQuestionChange} onEnd={handleNavigatorModalToggle(false)} />
        </Modal>
      )}

      {isTimeExpiredModalOpen && (
        <Modal
          title="Time Expired"
          primaryButtonText={modals.MODAL_BUTTON_TYPES.Ok}
          onPrimaryButtonClick={handleTimeExpiredConfirm}
          onClose={handleTimeExpiredConfirm}
        >
          Your time on this section has expired. Timing has begun on the next section. Click OK to continue.
        </Modal>
      )}
    </>
  );
};
